import { gps } from "exifr/dist/lite.esm.mjs"

import maplibregl from "maplibre-gl"

import Hook from "../../utils/hook"
import { getCssCustomProperties } from "../../utils/custom-properties"
import { fetchTsSitesNearby } from "../../utils/ts"

const REPORT_MAP_SOURCE_ID = `report-sites-source`
const REPORT_MAP_LAYER_ID = `report-sites-layer`
const REPORT_MAP_SHAPES_SOURCE_ID = `report-shapes-source`
const REPORT_MAP_SHAPES_CASING_LAYER_ID = `report-shapes-casing-layer`
const REPORT_MAP_TRAILS_LAYER_ID = `report-trails-layer`
const REPORT_MAP_AREAS_LAYER_ID = `report-areas-layer`
// hovered and clicked: the pins, and the shapes by their casing, the widest
// of their lines (under both the trails' and the areas')
const REPORT_MAP_INTERACTIVE_LAYER_IDS = [
  REPORT_MAP_LAYER_ID,
  REPORT_MAP_SHAPES_CASING_LAYER_ID,
]
const REPORT_MAP_SELECTED_STATE = `reportSelectedSiteId`
const REPORT_MAP_HOVERED_STATE = `reportHoveredSiteId`
// px around the photo pin and the selected site's pin fitted into view: the
// pins stand above their position, the flag also to the right of it, where
// the map's zoom and fullscreen buttons are (60 px with their margin)
const REPORT_MAP_PADDING = { top: 64, bottom: 24, left: 32, right: 112 }
// the zoom the map opens at, on the selected site (the page's place)
const REPORT_MAP_ZOOM = 14
// km around the page's place within which the map shows the other sites of
// its organization, which a report can be about instead
const REPORT_MAP_RADIUS_KM = 20

export const SitePage = Hook(`SitePage`, {
  refs: [`map`],
  targets: [`mobileMapToggle`],
  values: { mobileMapActive: Boolean },
  actions: [
    {
      name: `showMobileMap`,
      target: `mobileMapToggle`,
      type: `click`,
    },
  ],

  enterMapFullscreenOnLoad: false,

  mapRefMounted(mapRef) {
    mapRef.addReceiveHandler(this.mapReceive.bind(this))
  },

  showMobileMapAction() {
    this.mobileMapActiveValue = true

    if (this.mapRef && this.mapRef.el.dataset.naturkartanInited)
      this.enterMapFullscreen()
    else this.enterMapFullscreenOnLoad = true
  },

  mapReceive(message) {
    switch (message.method) {
      case `loaded`: {
        if (this.enterMapFullscreenOnLoad) {
          this.enterMapFullscreenOnLoad = false
          this.enterMapFullscreen()
        }
        break
      }

      case `setFullscreen`: {
        this.mobileMapActiveValue = message.active
        break
      }
    }
  },

  enterMapFullscreen() {
    if (this.mapRef) this.mapRef.setFullscreen(true)
  },
})

// reads GPS coordinates from the EXIF data of selected/dropped report photos and
// sends the first valid pair to the LiveView (until the LiveView has a position)
export const SitePageReportImagesUpload = Hook(`SitePageReportImagesUpload`, {
  values: { eventName: String, hasPosition: Boolean },
  actions: [
    // `input` (not `change`): LiveView handles `input` on `window` and clears
    // multiple file inputs there, so the files are only readable before it
    // bubbles up to `window`, i.e. here on the hook root element
    {
      name: `filesInput`,
      type: `input`,
    },
    {
      name: `filesDrop`,
      type: `drop`,
    },
  ],

  mounted() {
    this.finding = false
  },

  filesInputAction(e) {
    if (e.target?.type === `file`) this.findPosition(e.target.files)
  },

  filesDropAction(e) {
    this.findPosition(e.dataTransfer?.files)
  },

  async findPosition(files) {
    if (this.hasPositionValue || this.finding || !files?.length) return
    this.finding = true

    // copy, as the file input may be reset while files are being read
    const list = [...files]

    try {
      // the server takes a photo's position only when it's near the report's
      // place, so the next photo's is tried until one is taken
      for (const file of list) {
        const position = await this.readPosition(file)
        if (position && (await this.positionAccepted(position))) return
      }
    } finally {
      this.finding = false
    }
  },

  positionAccepted(position) {
    return new Promise((resolve) =>
      this.pushEvent(this.eventNameValue, position, (reply) =>
        resolve(Boolean(reply?.accepted))
      )
    )
  },

  async readPosition(file) {
    try {
      const { latitude, longitude } = (await gps(file)) || {}
      const valid =
        Number.isFinite(latitude) &&
        Number.isFinite(longitude) &&
        Math.abs(latitude) <= 90 &&
        Math.abs(longitude) <= 180 &&
        !(latitude === 0 && longitude === 0)

      return valid ? { lat: latitude, lng: longitude } : null
    } catch {
      return null // not an image with readable EXIF data
    }
  },
})

// The report modal's map: the organization's sites within
// `REPORT_MAP_RADIUS_KM` of the page's place (`center`, [lng, lat]), the
// selected one (`siteId`) with its active pin. Clicking another pin (or its shape) sends it to the server
// (`eventName`, `%{"id" => id}`), which asks to confirm it; once confirmed,
// it's the `siteId` value.
// The report photos' position (`photoPosition`, [lng, lat]) has a pin too.
// The trails' and areas' shapes are faded, except the selected or hovered
// site's (styled like the maps app's).
// The sites are searched in Typesense once the map is first shown (the modal
// is hidden until opened).
export const SitePageReportMap = Hook(`SitePageReportMap`, {
  values: {
    siteId: Number,
    organizationId: Number,
    center: Array,
    locale: String,
    eventName: String,
    photoPosition: Array,
  },
  refs: [`maplibre`],
  targets: [`photoPin`],
  mapLoaded: false,
  sitesRequested: false,
  fittedPhotoPosition: null,
  resizeHandler: null,
  clickHandler: null,
  mouseMoveHandler: null,
  mouseLeaveHandler: null,
  hoveredSiteId: null,
  popup: null,
  photoMarker: null,

  mounted() {
    this.abortController = new AbortController()
    this.cssCustomProps = getCssCustomProperties([
      `--color-white`,
      `--color-russet`,
      `--color-sea-green`,
    ])
  },

  destroyed() {
    this.abortController.abort()
    this.removeMapHandlers()
    this.photoMarker?.remove()
    this.photoMarker = null
  },

  maplibreRefMounted(maplibreRef) {
    this.mapLoaded = Boolean(maplibreRef.map?.isStyleLoaded?.())

    if (this.mapLoaded) {
      this.mapReady()
    } else {
      maplibreRef.addLoadHandler(() => {
        this.mapLoaded = true
        this.mapReady()
      })
    }
  },

  maplibreRefDestroyed() {
    this.removeMapHandlers()
    this.photoMarker?.remove()
    this.photoMarker = null
    this.mapLoaded = false
    this.sitesRequested = false
    this.fittedPhotoPosition = null
  },

  siteIdValueChanged() {
    this.showSelected()
  },

  photoPositionValueChanged() {
    this.showPhotoPin()
  },

  mapReady() {
    if (!this.maplibreRef) return

    const map = this.maplibreRef.map
    map.jumpTo({ center: this.centerValue, zoom: REPORT_MAP_ZOOM })

    this.resizeHandler = () => this.requestSites()
    map.on(`resize`, this.resizeHandler)
    this.requestSites()
    this.showPhotoPin()
  },

  // a DOM marker like the maps app's custom pin, from the template rendered
  // by the server; brought into view when it's outside it
  showPhotoPin() {
    if (!this.maplibreRef || !this.mapLoaded) return

    const map = this.maplibreRef.map
    const position = this.photoPositionValue

    if (!position) {
      this.photoMarker?.remove()
      this.photoMarker = null
      this.fittedPhotoPosition = null
      return
    }

    if (!this.photoMarker && this.photoPinTarget) {
      const element =
        this.photoPinTarget.content.firstElementChild.cloneNode(true)

      this.photoMarker = new maplibregl.Marker({
        element,
        anchor: `bottom-left`,
      })
    }

    this.photoMarker?.setLngLat(position).addTo(map)
    this.fitPhotoPin(position)
  },

  // the photo pin and the selected site's pin in view, once per position the
  // photo pin gets (and once the map has a size, i.e. the modal is open)
  fitPhotoPin(position) {
    const map = this.maplibreRef.map
    const key = position.join(`,`)
    if (key == this.fittedPhotoPosition || !map.getContainer().offsetWidth)
      return

    this.fittedPhotoPosition = key
    const selected =
      this.sitePoints?.get(this.siteIdValue)?.geometry.coordinates ||
      this.centerValue

    map.fitBounds(
      new maplibregl.LngLatBounds(position, position).extend(selected),
      { padding: REPORT_MAP_PADDING, maxZoom: 16 }
    )
  },

  // once the map has a size, i.e. the modal has been opened
  async requestSites() {
    if (this.sitesRequested || !this.maplibreRef) return

    const map = this.maplibreRef.map
    if (!map.getContainer().offsetWidth) return
    this.sitesRequested = true

    const [lng, lat] = this.centerValue
    let sites = { points: [], shapes: [] }

    try {
      sites = await fetchTsSitesNearby({
        lat,
        lng,
        radiusKm: REPORT_MAP_RADIUS_KM,
        organizationId: this.organizationIdValue,
        locale: this.localeValue,
        signal: this.abortController.signal,
      })
    } catch (error) {
      if (error.name == `AbortError`) return
      console.error(error)
    }

    if (!this.maplibreRef || !this.mapLoaded) return
    this.draw(sites)
  },

  draw({ points, shapes }) {
    const map = this.maplibreRef.map
    // the shapes' features have just the site's id
    this.sitePoints = new Map(points.map((point) => [point.id, point]))
    const selected = [`==`, [`id`], [`global-state`, REPORT_MAP_SELECTED_STATE]]
    const hovered = [`==`, [`id`], [`global-state`, REPORT_MAP_HOVERED_STATE]]
    const opacity = [`case`, [`any`, selected, hovered], 1, 0.5]
    // on top: the hovered site's, then the selected one's
    const sortKey = [`case`, hovered, 2, selected, 1, 0]
    const isKind = (kind) => [`==`, [`get`, `kind`], kind]

    map.addSource(REPORT_MAP_SHAPES_SOURCE_ID, {
      type: `geojson`,
      data: { type: `FeatureCollection`, features: shapes },
    })

    map.addLayer({
      id: REPORT_MAP_SHAPES_CASING_LAYER_ID,
      source: REPORT_MAP_SHAPES_SOURCE_ID,
      type: `line`,
      layout: { "line-sort-key": sortKey, "line-join": `round` },
      paint: {
        "line-width": 7,
        "line-color": this.cssCustomProps[`--color-white`],
        "line-opacity": opacity,
      },
    })

    map.addLayer({
      id: REPORT_MAP_AREAS_LAYER_ID,
      source: REPORT_MAP_SHAPES_SOURCE_ID,
      type: `line`,
      filter: isKind(`area`),
      layout: { "line-sort-key": sortKey, "line-join": `round` },
      paint: {
        "line-width": 3,
        "line-color": this.cssCustomProps[`--color-sea-green`],
        "line-opacity": opacity,
      },
    })

    map.addLayer({
      id: REPORT_MAP_TRAILS_LAYER_ID,
      source: REPORT_MAP_SHAPES_SOURCE_ID,
      type: `line`,
      filter: isKind(`trail`),
      layout: { "line-sort-key": sortKey, "line-join": `round` },
      paint: {
        "line-width": 5,
        "line-dasharray": [1, 0.2],
        "line-color": this.cssCustomProps[`--color-russet`],
        "line-opacity": opacity,
      },
    })

    map.addSource(REPORT_MAP_SOURCE_ID, {
      type: `geojson`,
      data: { type: `FeatureCollection`, features: points },
    })

    map.addLayer({
      id: REPORT_MAP_LAYER_ID,
      source: REPORT_MAP_SOURCE_ID,
      type: `symbol`,
      layout: {
        "icon-image": [
          `case`,
          selected,
          [`get`, `iconActive`],
          [`get`, `icon`],
        ],
        "icon-size": 0.55,
        "icon-anchor": `bottom`,
        "icon-allow-overlap": true,
        // on top: the hovered pin, then the selected one
        "symbol-sort-key": [`case`, hovered, 2, selected, 1, 0],
      },
    })

    this.showSelected()
    this.addMapHandlers()
    this.showPhotoPin()
  },

  showSelected() {
    if (this.maplibreRef && this.mapLoaded)
      this.maplibreRef.map.setGlobalStateProperty(
        REPORT_MAP_SELECTED_STATE,
        this.siteIdValue
      )
  },

  addMapHandlers() {
    const map = this.maplibreRef.map

    this.clickHandler = (event) => {
      const id = event.features?.[0]?.id
      if (id == null || id === this.siteIdValue) return

      // the server asks to confirm it, the `siteId` value then follows
      this.pushEvent(this.eventNameValue, { id })
    }

    // the hovered site's name, above its pin or where its shape is hovered
    // (the popup lets the pointer through, so moving onto it keeps the site
    // hovered)
    this.mouseMoveHandler = (event) => {
      const feature = event.features?.[0]
      if (!feature || feature.id === this.hoveredSiteId) return

      this.hoveredSiteId = feature.id
      map.setGlobalStateProperty(REPORT_MAP_HOVERED_STATE, feature.id)
      map.getCanvas().style.cursor = `pointer`

      const isPin = feature.layer.id == REPORT_MAP_LAYER_ID
      const title = this.sitePoints?.get(feature.id)?.properties.title

      this.popup?.remove()
      this.popup = null
      if (!title) return

      this.popup = new maplibregl.Popup({
        offset: isPin ? 34 : 10,
        anchor: `bottom`,
        closeButton: false,
        closeOnClick: false,
      })
        .setLngLat(isPin ? feature.geometry.coordinates : event.lngLat)
        .setText(title)
        .addTo(map)
    }

    this.mouseLeaveHandler = () => {
      this.hoveredSiteId = null
      map.setGlobalStateProperty(REPORT_MAP_HOVERED_STATE, null)
      map.getCanvas().style.cursor = ``
      this.popup?.remove()
      this.popup = null
    }

    const layerIds = REPORT_MAP_INTERACTIVE_LAYER_IDS
    map.on(`click`, layerIds, this.clickHandler)
    map.on(`mousemove`, layerIds, this.mouseMoveHandler)
    map.on(`mouseleave`, layerIds, this.mouseLeaveHandler)
  },

  removeMapHandlers() {
    const map = this.maplibreRef?.map

    if (map) {
      const layerIds = REPORT_MAP_INTERACTIVE_LAYER_IDS
      if (this.resizeHandler) map.off(`resize`, this.resizeHandler)
      if (this.clickHandler) map.off(`click`, layerIds, this.clickHandler)
      if (this.mouseMoveHandler)
        map.off(`mousemove`, layerIds, this.mouseMoveHandler)
      if (this.mouseLeaveHandler)
        map.off(`mouseleave`, layerIds, this.mouseLeaveHandler)
    }

    this.resizeHandler = null
    this.clickHandler = null
    this.mouseMoveHandler = null
    this.mouseLeaveHandler = null
    this.hoveredSiteId = null
    this.popup?.remove()
    this.popup = null
  },
})
